import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import type { ColumnDef } from "@tanstack/react-table";
import type { ApiKeyItem } from "@/app/api-keys/type";
import { DataTableColumnHeader } from "@/components/custom/table/data-table-column-header";
import { getRelativeTimeString } from "@/lib/dates";
import { RevokeApiKeyButton } from "./revoke-button";

const baseApiKeyColumns: ColumnDef<ApiKeyItem>[] = [
  {
    id: "label",
    accessorKey: "label",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Label" />
    ),
    cell: ({ row }) => <span>{row.original.label || "No Label"}</span>,
    filterFn: (row, id, value) => {
      const cellValue = row.getValue(id)?.toString().toLowerCase() || "";
      return cellValue.includes(value.toLowerCase());
    },
  },
  {
    id: "preview",
    accessorKey: "preview",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Preview" />
    ),
    cell: ({ row }) => <span>...{row.original.preview}</span>,
  },
  {
    id: "created_at",
    accessorKey: "created_at",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Created At" />
    ),
    cell: ({ row }) => {
      const createdAt = new Date(row.original.created_at);
      return (
        <time
          dateTime={createdAt.toISOString()}
          title={createdAt.toUTCString()}
        >
          {getRelativeTimeString(createdAt)}
        </time>
      );
    },
  },
  {
    id: "expires_at",
    accessorKey: "expires_at",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Expires" />
    ),
    cell: ({ row }) => {
      const expiresAt = row.original.expires_at;

      if (!expiresAt) {
        return (
          <span className="text-muted-foreground text-sm italic">Never</span>
        );
      }

      const expirationDate = new Date(expiresAt);
      const isExpired = expirationDate < new Date();

      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger
              render={
                <time
                  className={
                    isExpired ? "text-red-600 dark:text-red-400" : undefined
                  }
                  dateTime={expirationDate.toISOString()}
                />
              }
            >
              {`${isExpired ? "Expired " : ""}${getRelativeTimeString(expirationDate)}`}
            </TooltipTrigger>
            <TooltipContent>
              <p>
                {expirationDate.toLocaleString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                  hour12: true,
                  timeZoneName: "short",
                })}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    },
    sortingFn: (rowA, rowB) => {
      const expiresA = rowA.original.expires_at;
      const expiresB = rowB.original.expires_at;

      // Both never expire - consider equal
      if (!(expiresA || expiresB)) {
        return 0;
      }

      // Only A never expires - sort to end
      if (!expiresA) {
        return 1;
      }

      // Only B never expires - sort to end
      if (!expiresB) {
        return -1;
      }

      // Both have expiration dates - sort by date
      return new Date(expiresA).getTime() - new Date(expiresB).getTime();
    },
  },
];

const actionsColumn: ColumnDef<ApiKeyItem> = {
  id: "actions",
  header: () => <div className="flex items-center space-x-2">Actions</div>,
  cell: ({ row }) => <RevokeApiKeyButton id={row.original.id} />,
};

// Revoke is the only row action, so without `api_keys_delete` the column is empty
// for every row — drop it entirely rather than render a blank column.
export const getApiKeyColumns = (
  canRevoke: boolean
): ColumnDef<ApiKeyItem>[] =>
  canRevoke ? [...baseApiKeyColumns, actionsColumn] : baseApiKeyColumns;
